import { useCallback, useEffect, useState } from 'react'
import { Loader2, Megaphone, Send, TriangleAlert } from 'lucide-react'
import { BoxTruckBroadcastProgress } from '@numeo/types-main-web-app'
import { fetchBroadcastProgress } from '../services/boxTruckLoadsApi'

/**
 * What became of a message that is too big to send in one go — a dispatcher's own blast, or today's
 * run of a scheduled mailing. Both are the same kind of thing and share one queue, so one strip shows
 * both; a run says which mailing it is.
 *
 * It leaves over the following minutes, driven by the scheduler rather than by the page — so without
 * this the operator has pressed send on a thousand drivers and has no way of knowing whether any of it
 * happened. Nothing else on either page can tell them: the conversations only show the part that has
 * already gone out, and look exactly the same whether the rest is on its way or was abandoned.
 *
 * It polls while anything is unfinished and stops when nothing is, because there is nothing to watch
 * then. Both endings age out: the server keeps the last few broadcasts forever, and a banner about
 * one from last Tuesday is noise on every visit to the page.
 */
const POLL_MS = 15_000
/**
 * How often to look when NOTHING is in flight — only where a run can begin without the operator doing
 * anything, which is the Mailing page: the scheduler starts one at its EST minute and the page has no
 * other way to find out. The SMS page leaves it off, because the only thing that queues a run there is
 * the operator's own send, and that bumps `refreshKey`.
 *
 * Two minutes against two indexed reads of a handful of small rows, and only while the tab is in front.
 */
const DEFAULT_IDLE_POLL_MS = 120_000
/** How long a finished broadcast is still worth showing: enough to read, not enough to become furniture. */
const KEEP_DONE_MS = 10 * 60 * 1000
/** A failure is worth a day — it is the one ending the operator has to do something about, and they
 *  may not be the person who queued it. */
const KEEP_FAILED_MS = 24 * 60 * 60 * 1000

const unfinished = (row: BoxTruckBroadcastProgress) => row.status === 'queued' || row.status === 'sending'

/** When it ENDED, falling back to when it was queued for a row written before the server sent one. */
const endedAt = (row: BoxTruckBroadcastProgress) => new Date(row.finishedAt ?? row.createdAt).getTime()

const worthShowing = (row: BoxTruckBroadcastProgress, now: number) => {
    if (unfinished(row)) return true
    // Aged from the ENDING, not from `createdAt`. A three-thousand-driver run takes half an hour, so
    // measured from when it was queued it was already "old" the moment it finished: the operator went
    // from "Sending · 2,850 done" straight to an empty strip and never saw the result.
    const age = now - endedAt(row)
    return age < (row.status === 'abandoned' ? KEEP_FAILED_MS : KEEP_DONE_MS)
}

export const BroadcastProgressStrip = ({ refreshKey = 0, watchIdle = false }: { refreshKey?: number; watchIdle?: boolean }) => {
    const [rows, setRows] = useState<BoxTruckBroadcastProgress[]>([])

    const read = useCallback(async () => {
        try {
            return await fetchBroadcastProgress()
        } catch (error) {
            // Silent TO THE OPERATOR on purpose: this is a status strip nobody asked for, and a failed
            // poll must not put an error over a page whose conversations are working. Not silent to
            // whoever is looking for why a strip is frozen — a persistent 500 has to leave a trace.
            console.error('[box-truck] broadcast progress poll failed', error)
            return null
        }
    }, [])

    useEffect(() => {
        let live = true
        let timer: ReturnType<typeof setTimeout> | undefined
        /**
         * Which polling chain is the current one.
         *
         * There is only ever meant to be ONE: a tick reads, then arms the next. But coming back to the
         * tab starts a tick while the previous one may still be awaiting its read, and when that one
         * came back it armed a timer too — two chains from then on, both polling for the rest of the
         * page's life, and only the newer one's timer still clearable. Every chain carries the
         * generation it was started in and does nothing once that is no longer the current one.
         */
        let generation = 0

        const arm = (gen: number, ms: number) => {
            if (!live || gen !== generation) return
            timer = setTimeout(() => void tick(gen), ms)
        }

        const tick = async (gen: number) => {
            // A tab nobody is looking at asks nothing. Left out, a console open overnight polls all
            // night for a strip on a screen that is behind three other windows.
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return arm(gen, POLL_MS)
            const items = await read()
            if (!live || gen !== generation) return
            if (items) setRows(items)
            // A FAILED read re-arms like any other. Treated as "nothing in flight" it froze the strip
            // on its last frame — "Sending · 300 done" for a run that may have finished half an hour
            // ago — for the rest of the page's life, after one blip.
            if (!items) return arm(gen, POLL_MS)
            if (items.some(unfinished)) return arm(gen, POLL_MS)
            // Nothing to watch. Where a run can start by itself, keep a slow heartbeat; otherwise stop,
            // and set ONE timer for the moment the last finished row stops being worth showing — with
            // no poll to re-render, it would sit there until the operator navigated away.
            if (watchIdle) return arm(gen, DEFAULT_IDLE_POLL_MS)
            const expiry = items.filter((row) => row.status === 'done').map((row) => endedAt(row) + KEEP_DONE_MS)
            if (expiry.length) {
                const after = Math.max(0, Math.max(...expiry) - Date.now())
                timer = setTimeout(() => setRows((prev) => prev.filter((row) => row.status !== 'done')), after)
            }
        }

        // Coming back to the tab should show the truth immediately, not in two minutes.
        const onVisible = () => {
            if (document.visibilityState !== 'visible') return
            // The chain in flight is ABANDONED rather than raced: clearing the timer alone leaves a
            // tick that is mid-read free to arm one of its own, which is the second chain.
            generation += 1
            if (timer) clearTimeout(timer)
            void tick(generation)
        }
        document.addEventListener('visibilitychange', onVisible)

        void tick(generation)
        return () => {
            live = false
            document.removeEventListener('visibilitychange', onVisible)
            if (timer) clearTimeout(timer)
        }
    }, [read, refreshKey, watchIdle])

    const now = Date.now()
    const shown = rows.filter((row) => worthShowing(row, now))
    if (!shown.length) return null

    return (
        <div className="flex flex-col gap-1 border-b border-border bg-default/40 px-6 py-2">
            {shown.map((row) => {
                const broken = row.status === 'abandoned'
                const done = row.sent + row.failed
                return (
                    <div key={row.id} className="flex items-center gap-2 text-xs text-foreground-muted">
                        {broken ? (
                            <TriangleAlert size={13} className="shrink-0 text-danger" />
                        ) : unfinished(row) ? (
                            <Loader2 size={13} className="shrink-0 animate-spin text-accent" />
                        ) : row.label ? (
                            <Megaphone size={13} className="shrink-0 text-accent" />
                        ) : (
                            <Send size={13} className="shrink-0 text-accent" />
                        )}
                        <span className="tabular-nums">
                            {/* Named, because a mailing run is nobody's doing right now: an operator who
                                sees "sending to 812" with no explanation reads it as their own send. */}
                            {row.label && <span className="font-medium text-foreground">{row.label}</span>}
                            {row.label && ' · '}
                            {broken
                                ? `Stopped after ${done.toLocaleString()} of ${row.total.toLocaleString()} — check ${row.service === 'sms' ? 'your Quo connection' : 'the drivers’ Telegram groups'}`
                                : unfinished(row)
                                  ? `Sending to ${row.total.toLocaleString()} drivers · ${done.toLocaleString()} done`
                                  : `Sent to ${row.sent.toLocaleString()} of ${row.total.toLocaleString()} drivers`}
                            {row.failed > 0 && ` · ${row.failed.toLocaleString()} could not be reached`}
                        </span>
                    </div>
                )
            })}
        </div>
    )
}
